import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../services/apiClient';
import { revenueService, ContractCreatePayload } from '../../services/revenueService';
import { DataListTable, type Column } from '../../components/crm/DataListTable';

interface LeadRow {
  id: string;
  tenant_name: string;
  name: string;
  email: string;
  source: string;
  status: string;
  revenue: string;
  created_at: string | null;
}

const CloseWonModal: React.FC<{
  lead: LeadRow;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ lead, onClose, onSuccess }) => {
  const [contractType, setContractType] = useState<'one_time' | 'recurring'>('one_time');
  const [totalValue, setTotalValue] = useState('');
  const [recurringAmount, setRecurringAmount] = useState('');
  const [recurringInterval, setRecurringInterval] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!totalValue || parseFloat(totalValue) <= 0) {
      setError('Total value is required and must be greater than 0.');
      return;
    }
    if (contractType === 'recurring' && (!recurringAmount || parseFloat(recurringAmount) <= 0)) {
      setError('Recurring amount is required for recurring contracts.');
      return;
    }
    setSubmitting(true);
    try {
      const payload: ContractCreatePayload = {
        lead_id: lead.id,
        contract_type: contractType,
        total_value: totalValue,
        start_date: startDate,
        end_date: endDate || undefined,
      };
      if (contractType === 'recurring') {
        payload.recurring_amount = recurringAmount;
        payload.recurring_interval = recurringInterval;
      }
      await revenueService.createContract(payload);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contract');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-surface-muted rounded-xl p-6 w-full max-w-md shadow-xl">
        <h3 className="text-lg font-semibold text-fg mb-4">Record contract – {lead.name}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-fg-muted mb-1">Contract Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setContractType('one_time')}
                className={`px-4 py-2 rounded text-sm font-medium ${
                  contractType === 'one_time'
                    ? 'bg-amber-500 text-fg'
                    : 'bg-surface-muted text-fg-muted'
                }`}
              >
                One-Time
              </button>
              <button
                type="button"
                onClick={() => setContractType('recurring')}
                className={`px-4 py-2 rounded text-sm font-medium ${
                  contractType === 'recurring'
                    ? 'bg-amber-500 text-fg'
                    : 'bg-surface-muted text-fg-muted'
                }`}
              >
                Recurring
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs text-fg-muted mb-1">Total Value ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={totalValue}
              onChange={(e) => setTotalValue(e.target.value)}
              className="w-full px-3 py-2 bg-surface-muted border border-border rounded text-fg"
              placeholder="e.g. 5000"
            />
          </div>
          {contractType === 'recurring' && (
            <>
              <div>
                <label className="block text-xs text-fg-muted mb-1">Recurring Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={recurringAmount}
                  onChange={(e) => setRecurringAmount(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-muted border border-border rounded text-fg"
                  placeholder="e.g. 500"
                />
              </div>
              <div>
                <label className="block text-xs text-fg-muted mb-1">Interval</label>
                <select
                  value={recurringInterval}
                  onChange={(e) => setRecurringInterval(e.target.value as 'monthly' | 'quarterly' | 'yearly')}
                  className="w-full px-3 py-2 bg-surface-muted border border-border rounded text-fg"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </>
          )}
          <div>
            <label className="block text-xs text-fg-muted mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 bg-surface-muted border border-border rounded text-fg"
            />
          </div>
          <div>
            <label className="block text-xs text-fg-muted mb-1">End Date (optional)</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 bg-surface-muted border border-border rounded text-fg"
            />
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-2 justify-end mt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded bg-surface-muted text-fg">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded bg-amber-500 text-fg font-medium disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Contract'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Leads: React.FC = () => {
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [closeWonLead, setCloseWonLead] = useState<LeadRow | null>(null);
  const [filters, setFilters] = useState({
    tenant: '',
    source: '',
    status: '',
    date_from: '',
    date_to: '',
  });

  const fetchLeads = async (pageNum: number = page) => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(pageNum) };
      if (filters.tenant) params.tenant = filters.tenant;
      if (filters.source) params.source = filters.source;
      if (filters.status) params.status = filters.status;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const data = await apiClient.get('/platform/admin/leads/', params);
      setLeads(data.results);
      setTotalPages(data.total_pages);
    } catch {
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchLeads(1);
  };

  const columns = useMemo<Column<LeadRow>[]>(
    () => [
      {
        id: 'tenant',
        header: 'Tenant',
        mobileLabel: 'Tenant',
        render: (l) => <span className="text-sm text-fg-muted">{l.tenant_name}</span>,
      },
      {
        id: 'name',
        header: 'Name',
        mobilePriority: 'primary',
        mobileLabel: 'Name',
        render: (l) => (
          <>
            <span className="text-sm text-fg">{l.name}</span>
            <br />
            <span className="text-fg-muted text-xs">{l.email}</span>
          </>
        ),
      },
      {
        id: 'source',
        header: 'Source',
        mobileLabel: 'Source',
        render: (l) => <span className="text-sm text-fg-muted">{l.source}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        mobileLabel: 'Status',
        render: (l) => <span className="text-sm text-fg-muted">{l.status}</span>,
      },
      {
        id: 'revenue',
        header: 'Revenue',
        mobileLabel: 'Revenue',
        render: (l) => <span className="text-sm text-fg-muted">${l.revenue}</span>,
      },
      {
        id: 'created',
        header: 'Created',
        mobileLabel: 'Created',
        render: (l) => (
          <span className="text-sm text-fg-muted">
            {l.created_at ? new Date(l.created_at).toLocaleString() : '-'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        mobileLabel: 'Actions',
        render: (l) => (
          <div data-row-action>
            {!(l.status === 'qualified' && parseFloat(l.revenue || '0') > 0) ? (
              <button
                onClick={() => setCloseWonLead(l)}
                className="px-2 py-1 rounded bg-amber-500 text-fg text-xs font-medium hover:bg-amber-600"
              >
                Record contract
              </button>
            ) : (
              <span className="text-fg-muted text-xs">—</span>
            )}
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-fg">Global Lead Inspection</h1>

      <form onSubmit={handleFilter} className="flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs text-fg-muted mb-1">Tenant ID</label>
          <input
            type="text"
            value={filters.tenant}
            onChange={(e) => setFilters((f) => ({ ...f, tenant: e.target.value }))}
            className="px-3 py-2 bg-surface-muted border border-border rounded text-fg text-sm"
            placeholder="UUID"
          />
        </div>
        <div>
          <label className="block text-xs text-fg-muted mb-1">Source</label>
          <input
            type="text"
            value={filters.source}
            onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
            className="px-3 py-2 bg-surface-muted border border-border rounded text-fg text-sm"
            placeholder="Google, Direct..."
          />
        </div>
        <div>
          <label className="block text-xs text-fg-muted mb-1">Status</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className="px-3 py-2 bg-surface-muted border border-border rounded text-fg text-sm"
          >
            <option value="">All</option>
            <option value="new">New</option>
            <option value="attempted">Attempted</option>
            <option value="contacted">Contacted</option>
            <option value="replied">Replied</option>
            <option value="qualified">Qualified</option>
            <option value="unqualified">Unqualified</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-fg-muted mb-1">From Date</label>
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
            className="px-3 py-2 bg-surface-muted border border-border rounded text-fg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-fg-muted mb-1">To Date</label>
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
            className="px-3 py-2 bg-surface-muted border border-border rounded text-fg text-sm"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-amber-500 text-fg font-medium rounded hover:bg-amber-600"
        >
          Filter
        </button>
      </form>

      <DataListTable<LeadRow>
        columns={columns}
        rows={leads}
        getRowKey={(l) => l.id}
        loading={loading}
        emptyContent={<div className="text-fg-muted p-4">No leads found.</div>}
      />
      {!loading && totalPages > 1 && (
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-4 py-2 rounded bg-surface-muted text-fg disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-fg-muted">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-4 py-2 rounded bg-surface-muted text-fg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}

      {closeWonLead && (
        <CloseWonModal
          lead={closeWonLead}
          onClose={() => setCloseWonLead(null)}
          onSuccess={() => fetchLeads(page)}
        />
      )}
    </div>
  );
};

export default Leads;
